import {
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnFiltersState,
  getSortedRowModel,
  getCoreRowModel,
  PaginationState,
  useReactTable,
  SortingState,
} from '@ui/components';
import { RequestFieldFilterType, EmployeeTableDataDto } from '@interfaces/models';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { getPaginatedEmployees } from '@api/employees';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { getTableRequest } from '@shared/utils';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useDebounce } from '@ui/hooks';
import { z } from 'zod';

import { getEmployeesColumns } from './employees-table-columns';
import { useEmployeeFilters } from './use-employees-filters';

export const EMPLOYEES_TABLE_FILTERS_DEFAULT_VALUES = {
  [RequestFieldFilterType.EMPLOYEE_PROFILE]: [],
  [RequestFieldFilterType.EMPLOYEE_SERVICE]: [],
};

const filterSchema = z.object({
  [RequestFieldFilterType.EMPLOYEE_PROFILE]: z.array(z.string()),
  [RequestFieldFilterType.EMPLOYEE_SERVICE]: z.array(z.string()),
});

interface UseEmployeesTableProps {
  onOpenRemoveEmployeeDialog: (employee: EmployeeTableDataDto) => void;
  onOpenEditEmployeeDrawer: (employee: EmployeeTableDataDto) => void;
}

export const useEmployeesTable = (props: UseEmployeesTableProps) => {
  const { onOpenRemoveEmployeeDialog, onOpenEditEmployeeDrawer } = props;

  // Use a ref to store the last known totalItems value.
  // This is used to avoid flickering when the data is loading.
  // I do not want to use placeholderData in useQuery because loader is not visible then when changing pagination.
  const totalItemsRef = useRef<number>(0);
  const { t } = useTranslation();

  const onEditEmployee = useCallback(
    (employee: EmployeeTableDataDto) => {
      onOpenEditEmployeeDrawer(employee);
    },
    [onOpenEditEmployeeDrawer],
  );

  const onRemoveEmployee = useCallback(
    (employee: EmployeeTableDataDto) => {
      onOpenRemoveEmployeeDialog(employee);
    },
    [onOpenRemoveEmployeeDialog],
  );

  const columns = useMemo(
    () => getEmployeesColumns(t, onEditEmployee, onRemoveEmployee),
    [t, onEditEmployee, onRemoveEmployee],
  );

  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const debouncedTableSearchQuery = useDebounce(tableSearchQuery, 800);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { filtersOptions, isLoading: isFiltersLoading } = useEmployeeFilters();

  const filtersFrom = useForm({
    resolver: zodResolver(filterSchema),
    defaultValues: EMPLOYEES_TABLE_FILTERS_DEFAULT_VALUES,
  });

  const tableFilters = filtersFrom.watch();

  const request = useMemo(
    () =>
      getTableRequest({
        search: debouncedTableSearchQuery,
        filters: tableFilters,
        pagination,
        sorting,
      }),
    [pagination, sorting, debouncedTableSearchQuery, tableFilters],
  );

  const {
    data: apiData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['employees-table', request],
    queryFn: () => getPaginatedEmployees(request),
  });

  // Update the ref whenever we get valid data
  useEffect(() => {
    if (apiData?.pagination?.totalItems !== undefined) {
      totalItemsRef.current = apiData.pagination.totalItems;
    }
  }, [apiData?.pagination?.totalItems]);

  // Get the current totalItems - prioritize actual data if available. Needed on first render.
  const totalItems =
    apiData?.pagination?.totalItems !== undefined
      ? apiData.pagination.totalItems
      : totalItemsRef.current;

  useDeepCompareEffect(() => {
    setPagination((current) => ({
      ...current,
      pageIndex: 0,
    }));
  }, [debouncedTableSearchQuery, tableFilters]);

  const table = useReactTable({
    data: apiData?.data ?? [],
    columns: columns,
    state: { pagination, sorting, globalFilter: tableSearchQuery, columnFilters, rowSelection },
    pageCount: Math.ceil(totalItems / pagination.pageSize),
    rowCount: totalItems,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    getRowId: (row) => row.id as unknown as string,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setTableSearchQuery,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  return {
    globalFilter: table.getState().globalFilter,
    columnsLength: columns.length,
    state: table.getState(),
    refetchTable: refetch,
    isFiltersLoading,
    filtersOptions,
    data: apiData,
    filtersFrom,
    totalItems,
    isLoading,
    table,
  };
};
